import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GlobalTheme from "../../../styles/global.theme.js";
import * as Styled from "../../../CompanyForm.styled";
import * as MUIStyled from "../../../MUIStyled.js";
import { DefaultLayout } from "../../common/Layout/DefaultLayout.js";
import {
    BackButton,
    ButtonText,
    ButtonPurpleNext,
} from "../../elements/Buttons/Buttons.js";
import CustomAlert from "../../elements/Alert/Alert";
import ProgressBarStepper from "../../elements/ProgressBar/ProgressBar.js";
import Loading from "../../elements/Loading/Loading.js";
import {
    getTradingAddress,
    postTradingAddress,
} from "../../../config/fetchAPI.js";
import { TradingDialog } from "./TradingDialog.js";
import { PlacesAutoComplete } from "./PlacesAutoComplete";

const currentStep = 2;
const totalSteps = 6;

export const TradingPage = ({ errors }) => {
    const [tradingAddress, setTradingAddress] = useState([]);
    const [postalAddress, setPostalAddress] = useState([]);
    const [updatedAddress, setUpdatedAddress] = useState([]);

    const [isTradingAddressUpdated, setIsTradingAddressUpdated] =
        useState(false);
    const [isPostalAddressUpdated, setIsPostalAddressUpdated] = useState(false);

    const [isTradingDialogOpen, setIsTradingDialogOpen] = useState(false);
    const [isPostalDialogOpen, setIsPostalDialogOpen] = useState(false);

    const [isTradingGoogleAddressDisabled, setIsTradingGoogleAddressDisabled] =
        useState(false);
    const [isPostalGoogleAddressDisabled, setIsPostalGoogleAddressDisabled] =
        useState(false);

    const [sameAddressCheckboxValue, setSameAddressCheckboxValue] =
        useState(true);
    const [isPostalAddressFieldDisabled, setIsPostalAddressFieldDisabled] =
        useState(true);

    const [tradingAddressErrorMsg, setTradingAddressErrorMsg] = useState("");
    const [postalAddressErrorMsg, setPostalAddressErrorMsg] = useState("");
    const [serverError, setServerError] = useState("");

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);

    const navigate = useNavigate();

    // check if trading address is empty, allow to use google places
    useEffect(() => {
        if (
            tradingAddress.streetAddress === "" ||
            tradingAddress.streetAddress === undefined
        ) {
            setIsTradingGoogleAddressDisabled(false);
        } else {
            setIsTradingGoogleAddressDisabled(true);
        }
    }, [tradingAddress]);

    // check if postal address is empty, allow to use google places
    useEffect(() => {
        if (
            postalAddress.streetAddress === "" ||
            postalAddress.streetAddress === undefined
        ) {
            setIsPostalGoogleAddressDisabled(false);
        } else {
            setIsPostalGoogleAddressDisabled(true);
        }
    }, [postalAddress]);

    // Sync postal address with trading address when checkbox is checked
    useEffect(() => {
        // Only sync if not loading addresses from API
        if (
            sameAddressCheckboxValue &&
            tradingAddress.streetAddress &&
            !isLoadingAddresses
        ) {
            setPostalAddress({
                id: 2,
                type: "postal",
                streetAddress: tradingAddress.streetAddress || "",
                subPremise: tradingAddress.subPremise || "",
                suburb: tradingAddress.suburb || "",
                state: tradingAddress.state || "",
                postcode: tradingAddress.postcode || "",
            });
        }
    }, [tradingAddress, sameAddressCheckboxValue, isLoadingAddresses]);

    const handleGetTradingAddress = async () => {
        try {
            let resultData = await getTradingAddress();
            const tradingAddress = resultData.find(
                (address) => address.type === "trading"
            );
            const postalAddress = resultData.find(
                (address) => address.type === "postal"
            );

            if (tradingAddress) {
                setTradingAddress(tradingAddress);
            }

            if (postalAddress) {
                setPostalAddress(postalAddress);

                // Check if postal address is different from trading address
                if (tradingAddress) {
                    const addressesAreSame =
                        postalAddress.streetAddress ===
                            tradingAddress.streetAddress &&
                        postalAddress.subPremise ===
                            tradingAddress.subPremise &&
                        postalAddress.suburb === tradingAddress.suburb &&
                        postalAddress.state === tradingAddress.state &&
                        postalAddress.postcode === tradingAddress.postcode;

                    setSameAddressCheckboxValue(addressesAreSame);
                    setIsPostalAddressFieldDisabled(addressesAreSame);
                }
            }
        } catch (error) {
            console.error("Error loading cached fields:", error);
        } finally {
            setIsLoading(false);
            setIsLoadingAddresses(false);
        }
    };

    const handlePostTradingAddress = async () => {
        try {
            const finalTradingAddress = isTradingAddressUpdated
                ? updatedAddress
                : tradingAddress;
            const finalPostalAddress = isPostalAddressUpdated
                ? updatedAddress
                : postalAddress;

            // Check if postal address has actual data
            const hasPostalData =
                finalPostalAddress?.streetAddress?.trim() &&
                finalPostalAddress?.postcode?.trim();

            if (finalTradingAddress && hasPostalData) {
                const result = await postTradingAddress(
                    finalTradingAddress,
                    finalPostalAddress
                );

                // Check if server returned validation errors
                if (result && Array.isArray(result) && result[0]?.errors) {
                    const errorObj = result[0].errors;

                    // Format error messages for display
                    const errorMessages = Object.entries(errorObj)
                        .map(([field, messages]) => {
                            return `${field}: ${messages.join(", ")}`;
                        })
                        .join("; ");

                    setServerError(errorMessages);
                    return false;
                }

                setServerError("");
                return true;
            }
            return true;
        } catch (error) {
            console.error("Error posting trading address:", error);
            setServerError("An error occurred while saving the address.");
            return false;
        }
    };

    useEffect(() => {
        handleGetTradingAddress();
        if (isTradingAddressUpdated) {
            handlePostTradingAddress();
            setIsTradingAddressUpdated(false);
        }
        if (isPostalAddressUpdated) {
            handlePostTradingAddress();
            setIsPostalAddressUpdated(false);
        }
        // eslint-disable-next-line
    }, [isTradingAddressUpdated, isPostalAddressUpdated]);

    const handleSubmit = async (e) => {
        setIsLoading(true);
        e.preventDefault();

        let hasError = false;

        // Reset error messages before checking
        setTradingAddressErrorMsg("");
        setPostalAddressErrorMsg("");
        setServerError("");

        if (
            // Trading address fields are empty
            (!tradingAddress.streetAddress?.trim() ||
                !tradingAddress.suburb?.trim() ||
                !tradingAddress.state?.trim() ||
                !tradingAddress.postcode?.trim()) &&
            // Postal address needs to be checked only if checkbox is unchecked (sameAddressCheckboxValue === null)
            sameAddressCheckboxValue === false &&
            (!postalAddress.streetAddress?.trim() ||
                !postalAddress.suburb?.trim() ||
                !postalAddress.state?.trim() ||
                !postalAddress.postcode?.trim())
        ) {
            // Set errors for both addresses if both are empty
            setTradingAddressErrorMsg("Trading address is required.");
            setPostalAddressErrorMsg("Postal address is required.");
        } else if (
            // if postal address is empty and checkbox is checked
            sameAddressCheckboxValue === true &&
            !postalAddress.streetAddress?.trim()
        ) {
            setPostalAddressErrorMsg("Postal address is required.");
        } else {
            // Set only the error message for the trading address if it's empty
            if (
                !tradingAddress.streetAddress?.trim() ||
                !tradingAddress.suburb?.trim() ||
                !tradingAddress.state?.trim() ||
                !tradingAddress.postcode?.trim()
            ) {
                setTradingAddressErrorMsg("Trading address is required.");
            }

            // Do not set error for postal address if it has any value
            if (
                sameAddressCheckboxValue === false &&
                (!postalAddress.streetAddress?.trim() ||
                    !postalAddress.suburb?.trim() ||
                    !postalAddress.state?.trim() ||
                    !postalAddress.postcode?.trim())
            ) {
                setPostalAddressErrorMsg("Postal address is required.");
                hasError = true;
            }
        }

        if (hasError) {
            setIsLoading(false);
            return;
        }

        setIsPostalAddressUpdated(true);
        const success = await handlePostTradingAddress();

        // Only navigate if there are no server errors
        if (success !== false) {
            navigate("/contactDetail");
        } else {
            setIsLoading(false);
        }
    };

    // GooglePlaces
    const handleAutocompleteAddressChange = async (
        isTradingAddress,
        updatedValues,
        placeID
    ) => {
        if (updatedValues === "") {
            if (isTradingAddress) {
                setTradingAddress({
                    id: 1,
                    type: "trading",
                    streetAddress: "",
                    subPremise: "",
                    suburb: "",
                    state: "",
                    postcode: "",
                });
            } else {
                setPostalAddress({
                    id: 2,
                    type: "postal",
                    streetAddress: "",
                    subPremise: "",
                    suburb: "",
                    state: "",
                    postcode: "",
                });
            }
            return;
        }

        const splitAddressValues = updatedValues.split(",");
        const streetAddress = splitAddressValues[0];
        let subPremise = "";
        if (splitAddressValues.length === 4) {
            subPremise = splitAddressValues[1];
        }
        const suburbAndState =
            splitAddressValues[splitAddressValues.length - 2].split(" ");
        const suburb = suburbAndState.slice(0, -1).join(" ");
        const state = suburbAndState[suburbAndState.length - 1];

        let postcode = "";
        // get postcode from google maps api
        try {
            let result = await getPostcodeGoogleAPI(placeID);
            postcode = result || "";
        } catch (error) {
            console.error(error);
        }

        if (isTradingAddress) {
            setTradingAddress({
                id: 1,
                type: "trading",
                streetAddress: streetAddress.trim(),
                subPremise: subPremise.trim(),
                suburb: suburb.trim(),
                state: state.trim(),
                postcode: (postcode || "").trim(),
            });
        } else {
            setPostalAddress({
                id: 2,
                type: "postal",
                streetAddress: streetAddress.trim(),
                subPremise: subPremise.trim(),
                suburb: suburb.trim(),
                state: state.trim(),
                postcode: (postcode || "").trim(),
            });
        }
    };

    const getPostcodeGoogleAPI = async (placeId) => {
        return new Promise((resolve, reject) => {
            try {
                const service = new window.google.maps.places.PlacesService(
                    document.createElement("div")
                );

                service.getDetails(
                    {
                        placeId,
                        fields: ["address_components"],
                    },
                    (place, status) => {
                        if (
                            status ===
                                window.google.maps.places.PlacesServiceStatus
                                    .OK &&
                            place?.address_components
                        ) {
                            const postalCodeComponent =
                                place.address_components.find((comp) =>
                                    comp.types.includes("postal_code")
                                );

                            const postalCode = postalCodeComponent
                                ? postalCodeComponent.long_name
                                : null;

                            resolve(postalCode);
                        } else {
                            resolve(null);
                        }
                    }
                );
            } catch (err) {
                reject(err);
            }
        });
    };

    const handleManualAddressChange = (newAddress) => {
        setUpdatedAddress(newAddress);
    };

    const handleCheckboxChange = () => {
        const newValue = !sameAddressCheckboxValue;
        setSameAddressCheckboxValue(newValue);
        setIsPostalAddressFieldDisabled(newValue);
        if (newValue) {
            setPostalAddress({
                id: 2,
                type: "postal",
                streetAddress: tradingAddress.streetAddress || "",
                subPremise: tradingAddress.subPremise || "",
                suburb: tradingAddress.suburb || "",
                state: tradingAddress.state || "",
                postcode: tradingAddress.postcode || "",
            });
        } else {
            setPostalAddress({
                id: 2,
                type: "postal",
                streetAddress: "",
                subPremise: "",
                suburb: "",
                state: "",
                postcode: "",
            });
            setIsPostalAddressUpdated(false);
        }
    };

    const handleBack = () => {
        navigate("/companyDetail");
    };

    const handleTradingDialogOpen = () => {
        setIsTradingDialogOpen(true);
        setIsPostalDialogOpen(false);
    };

    const handlePostalDialogOpen = () => {
        setIsPostalDialogOpen(true);
        setIsTradingDialogOpen(false);
    };
    const handleCloseTradingDialog = () => {
        setIsTradingDialogOpen(false);
    };

    const handleClosePostalDialog = () => {
        setIsPostalDialogOpen(false);
    };

    return (
        <DefaultLayout currentStep={currentStep}>
            {isLoading ? (
                <Loading />
            ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <MUIStyled.CustomContainer>
                        <Styled.MainTitleArrowWrapper>
                            <BackButton
                                onClick={handleBack}
                                sx={{ marginLeft: "10%" }}
                            />
                            <Styled.MainTitle style={{ margin: "8px 0 0 0" }}>
                                Trading address
                            </Styled.MainTitle>
                        </Styled.MainTitleArrowWrapper>

                        <form onSubmit={handleSubmit}>
                            <Styled.TradingAddressBox>
                                {/* Conditionally render PlacesAutoComplete based on openDialog */}
                                {!isTradingDialogOpen && (
                                    <PlacesAutoComplete
                                        tradingAddress={tradingAddress}
                                        isTradingAddress={true}
                                        addressDisabled={
                                            isTradingGoogleAddressDisabled
                                        }
                                        onAddressChange={
                                            handleAutocompleteAddressChange
                                        }
                                        errorMessage={tradingAddressErrorMsg} // Pass error message
                                        error={tradingAddressErrorMsg !== ""}
                                    />
                                )}
                                {isTradingDialogOpen && (
                                    <TradingDialog
                                        open={isTradingDialogOpen}
                                        onClose={handleCloseTradingDialog}
                                        title="Trading Address"
                                        isTradingAddress={true}
                                        onAddressChange={
                                            handleManualAddressChange
                                        }
                                        tradingAddress={tradingAddress}
                                        setTradingAddress={setTradingAddress}
                                        setIsTradingAddressUpdated={
                                            setIsTradingAddressUpdated
                                        }
                                        setIsPostalAddressUpdated={
                                            setIsPostalAddressUpdated
                                        }
                                    />
                                )}
                                <ButtonText
                                    onClick={handleTradingDialogOpen}
                                    style={{
                                        display: "flex",
                                        alignSelf: "flex-start",
                                    }}>
                                    {isTradingDialogOpen
                                        ? ""
                                        : "Enter an address manually"}
                                </ButtonText>
                            </Styled.TradingAddressBox>

                            <Styled.TradingAddressBox>
                                {!isPostalDialogOpen && (
                                    <Styled.PostalBox>
                                        <Styled.StyledSpan>
                                            Same as trading address
                                        </Styled.StyledSpan>
                                        <Styled.StyledCheckbox
                                            checked={sameAddressCheckboxValue}
                                            onChange={handleCheckboxChange}
                                        />
                                    </Styled.PostalBox>
                                )}

                                {!isPostalDialogOpen && (
                                    <PlacesAutoComplete
                                        postalAddress={postalAddress}
                                        isTradingAddress={false}
                                        addressDisabled={
                                            isPostalGoogleAddressDisabled
                                        }
                                        onAddressChange={
                                            handleAutocompleteAddressChange
                                        }
                                        errorMessage={postalAddressErrorMsg}
                                        error={postalAddressErrorMsg !== ""}
                                    />
                                )}
                                {isPostalDialogOpen && (
                                    <TradingDialog
                                        open={!isPostalDialogOpen}
                                        title="Postal Address"
                                        onClose={handleClosePostalDialog}
                                        onAddressChange={
                                            handleManualAddressChange
                                        }
                                        isTradingAddress={false}
                                        postalAddress={postalAddress}
                                        setPostalAddress={setPostalAddress}
                                        setIsTradingAddressUpdated={
                                            setIsTradingAddressUpdated
                                        }
                                        setIsPostalAddressUpdated={
                                            setIsPostalAddressUpdated
                                        }
                                    />
                                )}

                                <ButtonText
                                    onClick={() =>
                                        !isPostalAddressFieldDisabled &&
                                        handlePostalDialogOpen(false)
                                    }
                                    style={{
                                        display: "flex",
                                        alignSelf: "flex-start",
                                        color: isPostalAddressFieldDisabled
                                            ? GlobalTheme.palette.grey.primary
                                                  .darker
                                            : GlobalTheme.palette.purple.primary
                                                  .main,
                                    }}>
                                    {isPostalDialogOpen ||
                                    isPostalAddressFieldDisabled
                                        ? ""
                                        : "Enter an address manually"}
                                </ButtonText>
                            </Styled.TradingAddressBox>

                            {serverError && (
                                <CustomAlert message={serverError} />
                            )}

                            <MUIStyled.CustomGrid
                                container
                                justifyContent={{
                                    xs: "center",
                                    sm: "flex-end",
                                }}>
                                <ButtonPurpleNext
                                    type="submit"
                                    style={{ marginTop: "40px" }}
                                    disabled={
                                        !postalAddress.streetAddress ||
                                        !tradingAddress.streetAddress
                                    }>
                                    {isLoading ? (
                                        <MUIStyled.CustomCircularProgress
                                            size={24}
                                        />
                                    ) : (
                                        "Next"
                                    )}
                                </ButtonPurpleNext>
                            </MUIStyled.CustomGrid>
                        </form>
                    </MUIStyled.CustomContainer>

                    <Styled.ProgressBarContainerWrapper>
                        {currentStep <= totalSteps && (
                            <ProgressBarStepper
                                currentStep={currentStep}
                                totalSteps={totalSteps}
                            />
                        )}
                    </Styled.ProgressBarContainerWrapper>
                </div>
            )}
        </DefaultLayout>
    );
};
